/**
 * Tasks Page
 * 
 * Centralized task management showing all tasks across the application.
 * Supports Demo mode (mock data via useTaskBoard) and Live mode (API via useTasksData).
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCw, AlertCircle, Clock, Calendar, ListTodo, User, Loader2 } from 'lucide-react';
import { useTaskBoard } from '@/hooks/useTaskBoard';
import { useTasksList, useCreateTaskMutation, useUpdateTaskMutation, useCompleteTaskMutation } from '@/hooks/useTasksData';
import { useUsersList } from '@/hooks/useUsersData';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { TaskFiltersComponent } from '@/components/task-board/task-filters';
import { TaskListView } from '@/components/task-board/task-list-view';
import { TaskCalendarView } from '@/components/task-board/task-calendar-view';
import { CreateTaskDrawer, type NewTaskData, type CreateTaskUser } from '@/components/task-board/create-task-drawer';
import { useMode } from '@/providers/ModeProvider';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { TaskFilters, TaskType, TaskPriority } from '@/types/tasks';
import type { TasksApiQueryParams, UpdateTaskApiRequest } from '@/types/api/tasks';

type ViewMode = 'list' | 'calendar';

type QuickFilter = 'my-tasks' | 'overdue' | 'due-today' | 'due-this-week';

// Mock current user - used in demo mode only
const CURRENT_USER = 'Sarah Johnson';

/**
 * Build API query params from active quick filters and compound filters.
 * Search is handled client-side and not included here.
 */
function buildApiParams(
  quickFilters: Set<QuickFilter>,
  compoundFilters: { type?: TaskType[]; priority?: TaskPriority[] }
): TasksApiQueryParams {
  const params: TasksApiQueryParams = {
    page: 1,
    pageSize: 200,
  };

  if (quickFilters.has('my-tasks')) {
    params.self = true;
  }

  if (quickFilters.has('overdue')) {
    params.isOverdue = true;
  }

  if (quickFilters.has('due-today')) {
    params.isDueToday = true;
  }

  if (quickFilters.has('due-this-week')) {
    params.isDueThisWeek = true;
  }

  if (compoundFilters.type && compoundFilters.type.length > 0) {
    params.taskType = compoundFilters.type.join(',');
  }

  if (compoundFilters.priority && compoundFilters.priority.length > 0) {
    params.priority = compoundFilters.priority.join(',');
  }

  return params;
}

export default function TaskBoardPage() {
  const { isDemo } = useMode();

  // ---------- Demo mode state (existing useTaskBoard) ----------
  const demoBoard = useTaskBoard();

  // ---------- Live mode state ----------
  const [activeQuickFilters, setActiveQuickFilters] = useState<Set<QuickFilter>>(new Set(['my-tasks']));
  const [compoundFilters, setCompoundFilters] = useState<{ type?: TaskType[]; priority?: TaskPriority[] }>({});
  const [searchQuery, setSearchQuery] = useState('');

  const apiParams = useMemo(
    () => (isDemo ? undefined : buildApiParams(activeQuickFilters, compoundFilters)),
    [isDemo, activeQuickFilters, compoundFilters]
  );

  const liveQuery = useTasksList(isDemo ? undefined : apiParams);

  // ---------- Shared UI state ----------
  // View mode kept for future calendar view support
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  // ---------- Demo mode: apply default filter on mount ----------
  useEffect(() => {
    if (isDemo) {
      applyDemoQuickFilters(new Set(['my-tasks']));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  // ---------- Demo mode quick-filter application ----------
  const applyDemoQuickFilters = useCallback((quickFilters: Set<QuickFilter>) => {
    const newFilters: TaskFilters = {};

    if (demoBoard.filters.searchQuery) {
      newFilters.searchQuery = demoBoard.filters.searchQuery;
    }

    if (quickFilters.size === 0) {
      demoBoard.replaceFilters(demoBoard.filters.searchQuery ? { searchQuery: demoBoard.filters.searchQuery } : {});
      return;
    }

    if (quickFilters.has('my-tasks')) {
      newFilters.assignedTo = [CURRENT_USER];
    }

    if (quickFilters.has('overdue')) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const veryOldDate = new Date('2000-01-01');
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(23, 59, 59, 999);
      newFilters.dateRange = { start: veryOldDate, end: yesterday };
      newFilters.status = ['open'];
    }

    if (quickFilters.has('due-today')) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endOfToday = new Date(today);
      endOfToday.setHours(23, 59, 59, 999);
      newFilters.dateRange = { start: today, end: endOfToday };
    }

    if (quickFilters.has('due-this-week')) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextWeek.setHours(23, 59, 59, 999);
      newFilters.dateRange = { start: today, end: nextWeek };
    }

    demoBoard.replaceFilters(newFilters);
  }, [demoBoard]);

  // ---------- Toggle quick filter (both modes) ----------
  const toggleQuickFilter = (filter: QuickFilter) => {
    const newFilters = new Set(activeQuickFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveQuickFilters(newFilters);

    if (isDemo) {
      applyDemoQuickFilters(newFilters);
    }
    // In live mode, apiParams is recomputed via useMemo and triggers a refetch
  };

  // ---------- Compound filter handlers (live mode) ----------
  const handleFiltersChange = (partial: Partial<TaskFilters>) => {
    if (isDemo) {
      demoBoard.setFilters(partial);
      return;
    }

    // In live mode, handle type/priority as compound API filters and search as client-side
    if (partial.type !== undefined) {
      setCompoundFilters(prev => ({ ...prev, type: partial.type }));
    }
    if (partial.priority !== undefined) {
      setCompoundFilters(prev => ({ ...prev, priority: partial.priority }));
    }
    if (partial.searchQuery !== undefined) {
      setSearchQuery(partial.searchQuery ?? '');
    }
  };

  const handleClearFilters = () => {
    if (isDemo) {
      demoBoard.clearFilters();
    } else {
      setCompoundFilters({});
      setSearchQuery('');
    }
    setActiveQuickFilters(new Set());
  };

  // ---------- Compute displayed tasks ----------
  const liveTasks = useMemo(() => {
    const tasks = liveQuery.data ?? [];
    if (!searchQuery.trim()) return tasks;
    const query = searchQuery.toLowerCase();
    return tasks.filter(
      t =>
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.customerName.toLowerCase().includes(query)
    );
  }, [liveQuery.data, searchQuery]);

  const displayedTasks = isDemo ? demoBoard.allTasks : liveTasks;
  const totalCount = displayedTasks.length;
  const isLoading = !isDemo && liveQuery.isLoading;

  // Filters object for the TaskFiltersComponent
  const currentFilters: TaskFilters = isDemo
    ? demoBoard.filters
    : {
        type: compoundFilters.type,
        priority: compoundFilters.priority,
        searchQuery: searchQuery || undefined,
      };

  // ---------- Refresh ----------
  const handleRefresh = () => {
    if (isDemo) {
      demoBoard.refreshTasks();
    } else {
      liveQuery.refetch();
    }
  };

  // ---------- Task status change ----------
  const handleTaskStatusChange = (taskId: string, newStatus: string) => {
    if (isDemo) {
      demoBoard.handleTaskStatusChange(taskId, newStatus as 'open' | 'completed');
    }
    // In live mode, status changes would go through the API (not part of this scope)
  };

  // ---------- Create task (live mode) ----------
  const createTaskMutation = useCreateTaskMutation();

  // ---------- Update / Complete task (live mode) ----------
  const updateTaskMutation = useUpdateTaskMutation();
  const completeTaskMutation = useCompleteTaskMutation();

  // Load users from API for the Create Task drawer (live mode only)
  const { data: apiUsers = [] } = useUsersList();

  const drawerUsers: CreateTaskUser[] | undefined = isDemo
    ? undefined
    : apiUsers
        .filter((u) => u.status === 'active')
        .map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}`, avatar: u.avatar }));

  const handleUpdateTask = (taskId: string, data: UpdateTaskApiRequest) => {
    if (isDemo) return;
    updateTaskMutation.mutate(
      { taskId: parseInt(taskId, 10), data },
      {
        onSuccess: () => {
          toast.success('Task updated successfully', { position: 'bottom-right' });
          handleRefresh();
        },
        onError: (error) => {
          toast.error(`Failed to update task: ${error.message}`, { position: 'bottom-right' });
        },
      },
    );
  };

  const handleCompleteTask = (taskId: string) => {
    if (isDemo) return;
    completeTaskMutation.mutate(
      parseInt(taskId, 10),
      {
        onSuccess: () => {
          toast.success('Task completed successfully', { position: 'bottom-right' });
          handleRefresh();
        },
        onError: (error) => {
          toast.error(`Failed to complete task: ${error.message}`, { position: 'bottom-right' });
        },
      },
    );
  };

  const handleCreateTask = (taskData: NewTaskData) => {
    if (isDemo) {
      // Demo mode: show mock confirmation and refresh demo board
      toast.success('Task created successfully', {
        description: `Task ID: TASK-${Math.floor(1000 + Math.random() * 9000)}`,
        position: 'bottom-right',
      });
      handleRefresh();
      return;
    }

    // Live mode: call the API
    createTaskMutation.mutate(
      {
        title: taskData.title,
        description: taskData.description || null,
        userAssignedId: parseInt(taskData.assignedToId, 10),
        dueDate: format(taskData.dueDate, 'yyyy-MM-dd'),
        customerId: taskData.customerId ? parseInt(taskData.customerId, 10) : null,
      },
      {
        onSuccess: () => {
          toast.success('Task created successfully', { position: 'bottom-right' });
          handleRefresh();
        },
        onError: (error) => {
          toast.error(`Failed to create task: ${error.message}`, { position: 'bottom-right' });
        },
      },
    );
  };

  const renderTaskContent = () => {
    if (isLoading) {
      return (
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 mx-auto text-muted-foreground mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading tasks...</p>
          </CardContent>
        </Card>
      );
    }

    if (totalCount === 0) {
      return (
        <Card>
          <CardContent className="p-12 text-center">
            <ListTodo className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Tasks Found</h3>
            <p className="text-muted-foreground mb-4">
              You don't have any active tasks at the moment. Tasks will appear here from:
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 max-w-md mx-auto">
              <li>• Active collection cases requiring follow-up</li>
              <li>• Promises to pay (active and broken)</li>
              <li>• Disputes requiring response</li>
              <li>• Unallocated receipts needing allocation</li>
            </ul>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        {viewMode === 'list' && (
          <TaskListView
            tasks={displayedTasks}
            onTaskStatusChange={handleTaskStatusChange}
            users={apiUsers}
            onSave={handleUpdateTask}
            onComplete={handleCompleteTask}
          />
        )}
        {viewMode === 'calendar' && (
          <TaskCalendarView
            tasks={displayedTasks}
            onTaskStatusChange={handleTaskStatusChange}
          />
        )}
      </>
    );
  };

  return (
    <div className="flex-1 space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Tasks</h1>
            <p className="text-muted-foreground mt-1">
              Track and complete collection activities across your organisation
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground">Quick Filters:</span>
          <Badge
            variant={activeQuickFilters.has('my-tasks') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('my-tasks') && "bg-primary text-primary-foreground"
            )}
            onClick={() => toggleQuickFilter('my-tasks')}
          >
            <User className="h-3 w-3 mr-1" />
            My Tasks
          </Badge>
          <Badge
            variant={activeQuickFilters.has('overdue') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('overdue') && "bg-red-600 text-white hover:bg-red-700"
            )}
            onClick={() => toggleQuickFilter('overdue')}
          >
            <AlertCircle className="h-3 w-3 mr-1" />
            Overdue
          </Badge>
          <Badge
            variant={activeQuickFilters.has('due-today') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('due-today') && "bg-orange-600 text-white hover:bg-orange-700"
            )}
            onClick={() => toggleQuickFilter('due-today')}
          >
            <Clock className="h-3 w-3 mr-1" />
            Due Today
          </Badge>
          <Badge
            variant={activeQuickFilters.has('due-this-week') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('due-this-week') && "bg-blue-600 text-white hover:bg-blue-700"
            )}
            onClick={() => toggleQuickFilter('due-this-week')}
          >
            <Calendar className="h-3 w-3 mr-1" />
            Due This Week
          </Badge>
        </div>

        {/* Advanced Filters */}
        <Card>
          <CardContent className="p-4">
            <TaskFiltersComponent
              filters={currentFilters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
              onCreateTask={() => setIsCreateDrawerOpen(true)}
            />
          </CardContent>
        </Card>

        {/* Task Content */}
        {renderTaskContent()}

      {/* Create Task Drawer */}
      <CreateTaskDrawer
        open={isCreateDrawerOpen}
        onOpenChange={setIsCreateDrawerOpen}
        onCreateTask={handleCreateTask}
        users={drawerUsers}
        isSubmitting={createTaskMutation.isPending}
      />
    </div>
  );
}
